import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import { Ridgeline } from "@/components/common/Ridgeline";
import {
  EDMONTON_COMMUNITIES,
  CALGARY_COMMUNITIES,
} from "@/data/communities";

/**
 * /neighbourhoods — two-city hub.
 * Edmonton and Calgary side by side. Each area card links to the shared
 * detail template at /neighbourhoods/:slug. Calgary areas render a small
 * "Coming soon" chip because their editorial detail is still pending.
 */

const CITIES = [
  {
    key: "calgary",
    name: "Calgary",
    intro:
      "Six neighbourhoods along the Bow — from the boutique streets of Mission to the vinyl shops of Inglewood.",
    hero:
      "https://images.unsplash.com/photo-1587547243081-6d1c04dbbbe6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    list: CALGARY_COMMUNITIES,
    scoutHint: "Full write-ups landing soon.",
  },
  {
    key: "edmonton",
    name: "Edmonton",
    intro:
      "Eight neighbourhoods along the river valley — mapped, walked, and honestly told.",
    hero:
      "https://images.unsplash.com/photo-1610134253150-a09b90d5b3a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    list: EDMONTON_COMMUNITIES,
    scoutHint: "Full write-ups + trail map at /relocating.",
    trailLink: "/relocating#terrain",
  },
];

export default function Neighbourhoods() {
  return (
    <div data-testid="page-neighbourhoods">
      {/* ============ HERO ============ */}
      <header
        className="relative"
        style={{ backgroundColor: "#EDE9E0" }}
        data-nav-theme="light"
      >
        <div className="mx-auto max-w-[1440px] px-6 pb-14 pt-[128px] md:px-10 md:pb-20 md:pt-[168px]">
          <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12">
            <div className="md:col-span-8">
              <Reveal>
                <Eyebrow>Neighbourhoods</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  className="font-display mt-7 max-w-[18ch]"
                  style={{
                    fontSize: "clamp(48px, 7.6vw, 108px)",
                    color: "#201E1A",
                    lineHeight: 0.95,
                    letterSpacing: "-0.015em",
                  }}
                  data-testid="hub-title"
                >
                  Two cities,{" "}
                  <span className="italic" style={{ color: "#C89A4E" }}>
                    honestly told.
                  </span>
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="body-measure mt-6 text-[17px] md:text-[19px]"
                  style={{ color: "rgba(32,30,26,0.75)" }}
                >
                  We only write about neighbourhoods we&rsquo;ve lived,
                  worked, and rented in. What follows is the read from the
                  ground — trade-offs and all.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Reveal delay={0.15}>
                <p
                  className="font-display"
                  style={{
                    fontSize: "clamp(40px, 4.5vw, 64px)",
                    color: "#24382E",
                    lineHeight: 0.9,
                  }}
                >
                  {EDMONTON_COMMUNITIES.length + CALGARY_COMMUNITIES.length}
                </p>
                <p
                  className="label-caps mt-2 text-[11px]"
                  style={{ color: "rgba(32,30,26,0.6)" }}
                >
                  Neighbourhoods on the list
                </p>
              </Reveal>
            </div>
          </div>
        </div>
        <div className="w-full opacity-70" aria-hidden>
          <Ridgeline stroke="#C89A4E" height={90} />
        </div>
      </header>

      {/* ============ CITY SECTIONS ============ */}
      {CITIES.map((city, ci) => (
        <section
          key={city.key}
          className="relative"
          style={{
            backgroundColor: ci % 2 === 0 ? "#F8F5EF" : "#EDE9E0",
          }}
          data-nav-theme="light"
          data-testid={`hub-city-${city.key}`}
        >
          <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
            <div className="grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-10">
              <div className="md:col-span-6">
                <Reveal>
                  <Eyebrow>{city.name}</Eyebrow>
                </Reveal>
                <Reveal delay={0.05}>
                  <h2
                    className="font-display mt-6 max-w-[16ch]"
                    style={{
                      fontSize: "clamp(38px, 5.2vw, 72px)",
                      color: "#201E1A",
                      lineHeight: 1,
                      letterSpacing: "-0.015em",
                    }}
                  >
                    {city.name}.
                  </h2>
                </Reveal>
                <Reveal delay={0.1}>
                  <p
                    className="mt-5 max-w-[46ch] text-[16px] leading-[1.6]"
                    style={{ color: "rgba(32,30,26,0.72)" }}
                  >
                    {city.intro}
                  </p>
                </Reveal>
              </div>
              <div className="md:col-span-5 md:col-start-8 md:text-right">
                <Reveal delay={0.1}>
                  <p
                    className="label-caps text-[10px]"
                    style={{ color: "rgba(32,30,26,0.55)" }}
                  >
                    {city.scoutHint}
                  </p>
                  {city.trailLink && (
                    <Link
                      to={city.trailLink}
                      className="group mt-3 inline-flex items-center gap-2"
                      style={{ color: "#24382E" }}
                    >
                      <span className="label-caps text-[11px]">
                        See the trail map
                      </span>
                      <ArrowUpRight
                        size={14}
                        className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </Link>
                  )}
                </Reveal>
              </div>
            </div>

            <ul
              className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8"
              data-testid={`hub-list-${city.key}`}
            >
              {city.list.map((n, i) => (
                <Reveal key={n.slug} as="li" delay={i * 0.03}>
                  <Link
                    to={`/neighbourhoods/${n.slug}`}
                    className="group relative block overflow-hidden"
                    style={{
                      borderRadius: 20,
                      boxShadow: "0 16px 36px -22px rgba(20,35,27,0.32)",
                    }}
                    data-testid={`hub-card-${n.slug}`}
                  >
                    <div
                      className="relative"
                      style={{ aspectRatio: "4 / 5" }}
                    >
                      <img
                        src={n.hero}
                        alt={n.name}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                      />
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(20,35,27,0.12) 0%, rgba(20,35,27,0.15) 45%, rgba(20,35,27,0.78) 100%)",
                        }}
                      />
                      {n.pending && (
                        <span
                          className="label-caps absolute right-4 top-4 rounded-full px-2.5 py-1 text-[10px]"
                          style={{
                            backgroundColor: "rgba(248,245,239,0.9)",
                            color: "#24382E",
                          }}
                        >
                          Coming soon
                        </span>
                      )}
                      <div className="absolute inset-x-4 bottom-4">
                        <h3
                          className="font-display"
                          style={{
                            color: "#F8F5EF",
                            fontSize: "clamp(24px, 3vw, 34px)",
                            lineHeight: 1,
                            textShadow: "0 2px 16px rgba(20,35,27,0.5)",
                          }}
                        >
                          {n.name.split(" / ")[0]}
                        </h3>
                        <p
                          className="mt-2 max-w-[32ch] text-[13px] leading-[1.4]"
                          style={{ color: "rgba(248,245,239,0.9)" }}
                        >
                          {n.vibe}
                        </p>
                        <div className="mt-3 flex items-center justify-between">
                          <span
                            className="label-caps text-[10px]"
                            style={{ color: "#E3B268" }}
                          >
                            {n.rent}
                          </span>
                          <ArrowUpRight
                            size={16}
                            color="#F8F5EF"
                            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>
      ))}
    </div>
  );
}
